import { Logo } from "../components/Logo";
import { Link } from "wouter";

export default function Landing() {
  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <Header />
      <main>
        <Hero />
        <DualFunnel />
        <FeaturesForClubs />
        <FeaturesForGolfers />
        <CtaSection />
      </main>
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-neutral-950/70 border-b border-neutral-900">
      <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
        <a href="/" className="inline-flex items-center" aria-label="Sportal home">
          <Logo size={28} withWordmark />
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-400">
          <Link href="/clubs" className="hover:text-neutral-0 transition-colors">Book a tee time</Link>
          <a href="#golfers" className="hover:text-neutral-0 transition-colors">For golfers</a>
          <a href="#clubs" className="hover:text-neutral-0 transition-colors">For clubs</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link
            href="/me/bookings"
            className="hidden sm:inline-flex items-center text-sm text-neutral-400 hover:text-neutral-0 transition-colors"
          >
            My bookings
          </Link>
          <Link
            href="/clubs"
            className="inline-flex items-center rounded-full bg-accent-500 text-neutral-950 px-4 py-1.5 text-sm font-medium hover:bg-accent-400 transition-colors"
          >
            Book now
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-50" aria-hidden />
      <div className="absolute inset-0 bg-spotlight" aria-hidden />
      <div className="relative mx-auto max-w-6xl px-6 pt-28 pb-32 sm:pt-36 sm:pb-40 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-neutral-800 bg-neutral-900/50 px-3 py-1 text-xs text-neutral-400 mb-8">
          <span className="size-1.5 rounded-full bg-accent-500" />
          Now onboarding clubs in Southern Africa
        </div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tightest text-balance text-neutral-0">
          Tee times,<br />
          <span className="text-accent-400">simplified.</span>
        </h1>
        <p className="mt-6 max-w-2xl mx-auto text-lg sm:text-xl text-neutral-400 text-pretty">
          The booking platform for premium golf clubs. Book your next round in seconds —
          clubs manage bookings, members and revenue from one calm dashboard.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            href="/clubs"
            className="inline-flex items-center justify-center w-full sm:w-auto rounded-full bg-accent-500 text-neutral-950 px-6 py-3 text-sm font-medium hover:bg-accent-400 transition-colors"
          >
            Book a tee time
          </Link>
          <a
            href="#clubs"
            className="inline-flex items-center justify-center w-full sm:w-auto rounded-full border border-neutral-700 bg-neutral-900/50 text-neutral-0 px-6 py-3 text-sm font-medium hover:bg-neutral-800 transition-colors"
          >
            For clubs — book a demo
          </a>
        </div>
      </div>
    </section>
  );
}

function DualFunnel() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-24">
      <div className="grid md:grid-cols-2 gap-6">
        <FunnelCard
          eyebrow="For golfers"
          title="Find a tee time. Book it. Play."
          body="See live availability across your home club and partner courses. Pay once, no callbacks, no holding for someone at reception."
          href="#golfers"
          cta="Get the app"
          accent
        />
        <FunnelCard
          eyebrow="For clubs"
          title="Run your bookings like a modern business."
          body="Replace the paper diary and the phone queue. Members and visitors book online; you watch revenue, course load and member behaviour in real time."
          href="#clubs"
          cta="Book a demo"
        />
      </div>
    </section>
  );
}

function FunnelCard({
  eyebrow, title, body, href, cta, accent = false,
}: {
  eyebrow: string; title: string; body: string; href: string; cta: string; accent?: boolean;
}) {
  return (
    <a
      href={href}
      className="group relative overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-900/40 p-8 hover:border-neutral-700 transition-colors"
    >
      <div className={`text-xs uppercase tracking-widest mb-4 ${accent ? "text-accent-400" : "text-neutral-500"}`}>
        {eyebrow}
      </div>
      <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tighter text-neutral-0 text-balance">
        {title}
      </h3>
      <p className="mt-4 text-neutral-400 text-pretty">{body}</p>
      <div className="mt-8 inline-flex items-center gap-2 text-sm text-neutral-0 group-hover:gap-3 transition-all">
        {cta}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </a>
  );
}

function FeaturesForGolfers() {
  return (
    <section id="golfers" className="border-t border-neutral-900">
      <div className="mx-auto max-w-6xl px-6 py-24">
        <div className="text-xs uppercase tracking-widest text-accent-400 mb-4">For golfers</div>
        <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-balance text-neutral-0 max-w-2xl">
          Your next round, three taps away.
        </h2>
        <p className="mt-4 text-neutral-400 max-w-xl text-pretty">
          Sportal lives in your pocket. Real-time tee sheets, instant booking, group invites, and your handicap on the home screen.
        </p>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          <FeatureRow title="Live tee sheets" body="See every open slot at your club and partner courses. Real-time, never stale." />
          <FeatureRow title="Group bookings" body="Invite playing partners with a link. They confirm, you all show up on the same tee." />
          <FeatureRow title="Member benefits" body="Your member rate, partner-course access and handicap stay in sync. One login, every course." />
        </div>

        <div className="mt-16 flex flex-col sm:flex-row gap-3">
          <StoreBadge platform="apple" />
          <StoreBadge platform="google" />
        </div>
      </div>
    </section>
  );
}

function FeaturesForClubs() {
  return (
    <section id="clubs" className="border-t border-neutral-900 bg-neutral-900/20">
      <div className="mx-auto max-w-6xl px-6 py-24">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">For clubs</div>
        <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-balance text-neutral-0 max-w-2xl">
          Operations, in one calm dashboard.
        </h2>
        <p className="mt-4 text-neutral-400 max-w-xl text-pretty">
          Replace the paper diary and the phone queue. Members and visitors book themselves; you see exactly how the course is running.
        </p>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureRow title="Live tee sheet" body="Drag-and-drop bookings, walk-ins, blocked slots. Every change auditable." />
          <FeatureRow title="Member management" body="Subscriptions, dependants, partner rights, handicap sync. Bulk import from Excel." />
          <FeatureRow title="Revenue at a glance" body="Daily/weekly/monthly takings, green fees vs cart vs F&B, exportable to CSV." />
          <FeatureRow title="Visitor pipeline" body="Capture visitor details, charge online, convert to members with one click." />
          <FeatureRow title="Comms" body="Bulk member emails for course closures, tournaments, and ladders. Open-rate tracking." />
          <FeatureRow title="Reports" body="Round counts, peak utilisation, no-show rates. The board pack writes itself." />
        </div>

        <div className="mt-16 rounded-2xl border border-neutral-800 bg-neutral-950/60 p-8">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <h3 className="font-display text-2xl font-semibold tracking-tighter text-neutral-0">
                See it on your course.
              </h3>
              <p className="mt-2 text-neutral-400 text-pretty">
                30-minute demo with your real bookings flow. No commitment, no PowerPoint.
              </p>
            </div>
            <a
              href="mailto:hello@sportalapp.com?subject=Demo%20request"
              className="inline-flex items-center justify-center rounded-full bg-accent-500 text-neutral-950 px-6 py-3 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap"
            >
              Book a demo
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureRow({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-950/40 p-6">
      <div className="size-8 rounded-lg bg-accent-500/10 flex items-center justify-center mb-4">
        <div className="size-2 rounded-full bg-accent-500" />
      </div>
      <h3 className="font-display text-lg font-semibold text-neutral-0 tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-neutral-400 text-pretty">{body}</p>
    </div>
  );
}

function StoreBadge({ platform }: { platform: "apple" | "google" }) {
  const label = platform === "apple" ? "App Store" : "Google Play";
  const sub = platform === "apple" ? "Download on the" : "Get it on";
  return (
    <div className="inline-flex items-center gap-3 rounded-xl border border-neutral-700 bg-neutral-900/60 px-5 py-3 text-neutral-300 cursor-not-allowed opacity-60" aria-label={`${label} (coming soon)`}>
      {platform === "apple" ? (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
        </svg>
      ) : (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3.6 20.4c-.4-.4-.6-.9-.6-1.5V5.1c0-.6.2-1.1.6-1.5l10 8.4-10 8.4zm11.1-9.3l-2.4-2.4 2.4-1.4 2.6 1.5-2.6 1.5v.8zm3.6 2.1l-3 1.7-2.4-2.4 2.4-2.4 3 1.7c.5.3.5 1.1 0 1.4zm-12.9-9l8.4 4.9-2.1 2.1-6.3-7zm0 16.6l6.3-7 2.1 2.1-8.4 4.9z"/>
        </svg>
      )}
      <div className="flex flex-col leading-tight text-left">
        <span className="text-[10px] uppercase tracking-wider text-neutral-500">{sub}</span>
        <span className="text-sm font-medium text-neutral-200">{label}</span>
      </div>
      <span className="ml-2 text-[10px] uppercase tracking-wider text-accent-400 border border-accent-800 rounded-full px-2 py-0.5">Soon</span>
    </div>
  );
}

function CtaSection() {
  return (
    <section id="contact" className="border-t border-neutral-900">
      <div className="mx-auto max-w-4xl px-6 py-32 text-center">
        <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-balance text-neutral-0">
          Bring your club into 2026.
        </h2>
        <p className="mt-4 text-neutral-400 text-pretty max-w-lg mx-auto">
          Onboard in under a week. Migrate your members, your visitor history, your green-fee structures. We do the heavy lifting.
        </p>
        <div className="mt-10">
          <a
            href="mailto:hello@sportalapp.com?subject=Demo%20request"
            className="inline-flex items-center rounded-full bg-accent-500 text-neutral-950 px-7 py-3.5 text-sm font-medium hover:bg-accent-400 transition-colors"
          >
            Talk to us
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-neutral-900">
      <div className="mx-auto max-w-6xl px-6 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
        <Logo size={24} withWordmark className="opacity-70" />
        <div className="flex items-center gap-6 text-sm text-neutral-500">
          <a href="mailto:hello@sportalapp.com" className="hover:text-neutral-300 transition-colors">hello@sportalapp.com</a>
          <a href="https://admin.sportalapp.com" className="hover:text-neutral-300 transition-colors">Admin</a>
          <span className="text-neutral-700">© 2026 Sportal</span>
        </div>
      </div>
    </footer>
  );
}
